import { useEffect } from 'react';
import { Button, Flex, Typography } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import { ButtonState } from '../common/constants';
import DoorControl from '../service/doorControl';
import {
  AlarmWarningLine,
  DoorClosedLine,
  DoorOpenLine,
  // RefreshLine,
  SignalCellularOffLine,
} from '../service/doorIcons';

// destructure imports
const { Text } = Typography;

/** Colour and Icon Configuration Map - see `_buttonspec.md` */
const STATE_THEME = {
  CONFLICT: {
    bgColour: '#fff2f0', // soft red
    borderColour: '#ff4d4f', // error red
    clickable: false,
    Icon: AlarmWarningLine,
    iconColour: '#ff4d4f',
    subColour: '#cf1322',
    subLabel: 'Conflict (>1 agent)',
    textColour: '#cf1322', // deep red
  },
  IN_USE: {
    bgColour: '#fffbe6', // warning yellow
    borderColour: '#faad14', // vibrant amber
    clickable: false,
    Icon: DoorOpenLine,
    iconColour: '#faad14',
    subColour: '#d48806',
    subLabel: (countdown) => `Open ${countdown}s`,
    textColour: '#d48806', // dark amber
  },
  OFFLINE: {
    bgColour: '#f5f5f5', // neutral grey
    borderColour: '#d9d9d9',
    clickable: false,
    Icon: SignalCellularOffLine,
    iconColour: '#d9d9d9',
    subColour: '#595959',
    subLabel: 'Offline',
    textColour: '#595959', // dark grey
  },
  READY: {
    bgColour: '#f6ffed', // green
    borderColour: '#52c41a',
    clickable: true,
    Icon: DoorClosedLine,
    iconColour: '#52c41a',
    subColour: '#237804',
    subLabel: 'Tap to Unlock',
    textColour: '#237804',
  },
};

// eslint-disable-next-line import/prefer-default-export
export function DoorButton({ doorId, siteId, ...door }) {
  const { countdown, doorState, errorMsg, loading, unlock } = DoorControl({
    doorId,
    siteId,
  });

  const { label } = door; // unpack the remaining props here

  const currentTheme = STATE_THEME[doorState] || STATE_THEME.OFFLINE;
  const { Icon } = currentTheme;

  const handleClick = async () => {
    if (!currentTheme.clickable || loading) return;
    await unlock();
  };

  useEffect(() => {
    if (errorMsg) console.error(errorMsg); // log the updated message
  }, [errorMsg]);

  return (
    <Flex align="center" gap="small" justify="center" vertical>
      <Button
        disabled={!currentTheme.clickable && doorState !== ButtonState.IN_USE}
        onClick={handleClick}
        style={{
          backgroundColor: currentTheme.bgColour,
          borderColor: currentTheme.borderColour,
          borderRadius: 20,
          borderWidth: 4,
          cursor: currentTheme.clickable ? 'pointer' : 'not-allowed',
          height: 128,
          width: 128,
        }}
        type={doorState === ButtonState.READY ? 'primary' : 'default'}
      >
        {loading ? (
          <LoadingOutlined
            style={{ color: currentTheme.textColour, fontSize: 48 }}
          />
        ) : (
          <Icon style={{ color: currentTheme.iconColour, fontSize: 48 }} />
        )}
      </Button>
      <Text strong style={{ color: currentTheme.textColour }}>
        {label || `Door ${doorId}`}
      </Text>
      <Text type="secondary">
        {typeof currentTheme.subLabel === 'function'
          ? currentTheme.subLabel(countdown)
          : currentTheme.subLabel}
      </Text>
    </Flex>
  );
}

DoorButton.propTypes = {
  doorId: PropTypes.string.isRequired,
  siteId: PropTypes.string.isRequired,
};
